import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { FilterService } from '../../../../../_shared/services';
import { ExecutiveStatsSummaryRequest } from '../../../../../_shared/models/driver-summary-request.model';
import { ExecutiveStatsService } from '../../../services/executive-stats.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { DonutChartComponent, DonutChartData } from '../../../../../_shared/components/graphs/donut-chart/donut-chart.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { GraphErrorStateComponent } from '../../../../../_shared/components/graphs/graph-error-state/graph-error-state.component';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-score-widget',
  standalone: true,
  imports: [CommonModule, DonutChartComponent, GraphTrendIconComponent, EmptyStateComponent, NgbDropdownModule, GraphErrorStateComponent, TranslocoDirective],
  templateUrl: './driver-score-widget.component.html',
  styleUrl: './driver-score-widget.component.scss',
})
export class DriverScoreWidgetComponent extends BaseWidgetComponent {
  private executiveStatsService = inject(ExecutiveStatsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  public isFiltering: boolean = false;
  public hasError: boolean = false;
  public errorMessage: string = '';
  public hasResult: boolean = false;
  public donutData: DonutChartData[] = [];
  public request: ExecutiveStatsSummaryRequest;

  // Hover state for segment highlighting
  hoveredPercentage: number | null = null;
  hoveredSegmentName: string | null = null;

  private readonly colors = {
    best: ['#316D28', '#3B8A2F', '#50AD41', '#72C464', '#A3DC99'],
    worst: ['#E46F2C', '#FF7F00', '#FF9500', '#FFB132', '#FFE5A5']
  };

  selectedOption: 'Best' | 'Worst' = 'Best';
  filterLabel: string = this.transloco.translate('executiveStats.driverScore.best');

  constructor() {
    const filterService = inject(FilterService);
    super(filterService);
    this.isLoading = true;
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;
    try {
      this.request = {
        driverIds: [...this.widgetFilter.driverIds],
        accountIds: [...this.widgetFilter.accountIds],
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
      };

      this.cancelSubject.next();
      const [_, scoreInsights] = await Promise.all([
        this.loadDriverScore(this.selectedOption),
        this.executiveStatsService.getDriverScoresInsightsSummary(this.request, this.cancelSubject)
      ]);

      this.value = scoreInsights.data?.totalValue ?? 0;
      this.percentageDifference = scoreInsights.data?.percentageDifference ?? 0;

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.hasError = true;
          this.errorMessage = message;
          this.isFiltering = false;
          this.isLoading = false;
        }
      });
    }
  }

  public override formatValue(value: number): string {
    return `${value.toFixed(0)}%`;
  }

  public formatDifference(value: number): string {
    return `${value.toLocaleString('default', { maximumFractionDigits: 2 })}%`;
  }

  formatPercentChange(): string {
    const sign = this.percentageDifference > 0 ? '+' : '';
    return `${sign}${this.percentageDifference.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  formatTotalValue(): string {
    return `${this.value.toFixed(0)}%`;
  }

  isUptrendPositive(): boolean {
    return true;
  }

  hasData(): boolean {
    return this.hasResult && this.donutData.length > 0;
  }

  public async loadDriverScore(option: 'Best' | 'Worst'): Promise<void> {
    this.selectedOption = option;
    this.filterLabel = this.transloco.translate(option === 'Best' ? 'executiveStats.driverScore.best' : 'executiveStats.driverScore.worst');
    this.isFiltering = true;

    const isBest = option === 'Best';
    const response = isBest
      ? await this.executiveStatsService.getBestDriverScores(this.request, this.cancelSubject)
      : await this.executiveStatsService.getWorstDriverScores(this.request, this.cancelSubject);

    const colorPalette = isBest ? this.colors.best : this.colors.worst;

    this.donutData = response.data?.map((item, index) => ({
      name: item.name,
      value: item.score,
      color: colorPalette[index % colorPalette.length]
    })) ?? [];

    this.hasResult = this.donutData.some(x => x.value > 0);
    this.isFiltering = false;
  }

  onFilterChange(option: 'Best' | 'Worst'): void {
    this.loadDriverScore(option);
  }

  onSegmentHover(event: { percentage: number; name: string } | null): void {
    if (event) {
      this.hoveredPercentage = event.percentage;
      this.hoveredSegmentName = event.name;
    } else {
      this.hoveredPercentage = null;
      this.hoveredSegmentName = null;
    }
  }

  getCenterPercentage(): string {
    return this.hoveredPercentage !== null ? `${this.hoveredPercentage}%` : this.transloco.translate('executiveStats.driverScore.percentagePlaceholder');
  }

  get legendItems() {
    return this.donutData.map(item => ({
      label: item.name,
      color: item.color,
      percentage: item.value.toFixed(0)
    }));
  }
}